import { ReactNode, useCallback, useMemo, useState } from "react";
import RectangleButtonCreatorPanel from "../../../components/Buttons/RectangleButtonCreatorPanel";
import HelpModal from "../../../components/Containers/HelpModal";
import { Colors } from "../../../utils/colors";
import StandardBorderedContainer from "../../../components/Containers/StandardBorderedContainer";
import {
  isBusinessSubscription,
  mostRecentSubscriptionPlan,
} from "@markit/common.utils";
import { useSelector } from "react-redux";
import { getAccountState } from "../../../redux/slices/accountSlice";
import { FormatCurrency } from "../../../utils/FormatCurrency";
import CreatorModePanelWrapper from "../../../components/CreatorDashboard/CreatorModePanelWrapper";
import { TabPanel } from "../../../components/FullEventSubComponents/TabPanel";
import BillingPanel from "../../../components/CreatorDashboard/Settings/BillingPanel";
import ProfileAccountDetails from "../../../components/CreatorDashboard/CreatorProfile/ProfileAccountDetails";
import BillingHistoryPanel from "../../../components/CreatorDashboard/Settings/BillingHistoryPanel";
import TeamMembersPanel from "../../../components/CreatorDashboard/TeamMembers/TeamMembersPanel";
import { OnboardingFlowType, TeamMemberRole } from "@markit/common.types";
import { useLocation } from "react-router-dom";
import ApiManagementPanel from "../../../components/CreatorDashboard/Settings/ApiManagementPanel";
import McpManagementPanel from "../../../components/CreatorDashboard/Settings/McpManagementPanel";
import { testStripe } from "../../../utils/stripeAccountUtils";
import OnboardingFlowDot from "../../../components/Onboarding/OnboardingFlowDot";

export enum CreatorSettingsTab {
  GENERAL = 0,
  BILLING = 1,
  HISTORY = 2,
  TEAM = 3,
  API = 4,
  MCP = 5,
}

enum SupportCreatorSettingsTabs {
  GENERAL = 0,
  TEAM = 1,
  MCP = 2,
}

const CreatorSettingsPanel = () => {
  const { accountData, teamMemberData } = useSelector(getAccountState).account;
  const { initialTabValue } = useLocation().state || {};
  // Support members have fewer tabs, so a link to the MCP tab lands on theirs.
  const [value, setValue] = useState<CreatorSettingsTab>(
    (teamMemberData?.role === TeamMemberRole.TWO_WAY_SUPPORT &&
    initialTabValue === CreatorSettingsTab.MCP
      ? SupportCreatorSettingsTabs.MCP
      : initialTabValue) || CreatorSettingsTab.GENERAL
  );
  const [helpModal, setHelpModal] = useState(false);
  const [showMcpTourFinalStep, setShowMcpTourFinalStep] = useState(false);

  const styles = {
    headerText: {
      fontSize: 32,
    },
  };

  const subscriptionPlan = useMemo(
    () => mostRecentSubscriptionPlan(accountData, testStripe(accountData.uid)),
    [accountData]
  );

  const trialSubscription = useMemo(
    () => accountData.customer.state === "TRIAL",
    [accountData.customer.state]
  );

  const supportTeamMember = useMemo(
    () => teamMemberData?.role === TeamMemberRole.TWO_WAY_SUPPORT,
    [teamMemberData]
  );

  const goToMcpTab = useCallback(() => setValue(CreatorSettingsTab.MCP), []);

  const goToBillingTab = useCallback(
    () => setValue(CreatorSettingsTab.BILLING),
    []
  );

  // Flags the MCP tab while the MCP & API onboarding tour is still incomplete
  const mcpTabRightIcons = useMemo(() => {
    const icons: ReactNode[] = [];
    icons[CreatorSettingsTab.MCP] = (
      <OnboardingFlowDot flowTypes={[OnboardingFlowType.MCP_API]} />
    );
    return icons;
  }, []);

  // The MCP & API tour ends on the API key card, so its last step switches tabs
  const continueMcpTourOnApiTab = useCallback(() => {
    setShowMcpTourFinalStep(true);
    setValue(CreatorSettingsTab.API);
  }, []);

  return (
    <>
      <CreatorModePanelWrapper
        title={
          <div className="ColumnNormal" style={{ gap: 20 }}>
            <span style={{ ...styles.headerText, maxWidth: 600 }}>
              Settings
            </span>
            <StandardBorderedContainer
              containerStyles={{ padding: 14, borderRadius: 12 }}
            >
              <div className="AlignedRow">
                <span className="bodySubtext">
                  You are currently {trialSubscription ? "trialing" : "on"} the{" "}
                  <span className="bodyMedium">{subscriptionPlan.name}</span>{" "}
                  Monthly plan for{" "}
                  <span className="bodyMedium">
                    {trialSubscription
                      ? FormatCurrency(0)
                      : FormatCurrency(subscriptionPlan.price / 100)}{" "}
                    per month
                  </span>
                  .{" "}
                  {isBusinessSubscription(accountData)
                    ? ""
                    : "Upgrade at any time."}
                </span>
              </div>
            </StandardBorderedContainer>
          </div>
        }
        headerRight={
          <RectangleButtonCreatorPanel
            title="Contact Support"
            onPress={() => setHelpModal(true)}
            iconName="ic:round-arrow-outward"
          />
        }
        headerTabs={{
          tabNames: supportTeamMember
            ? ["General", "Team", "MCP"]
            : ["General", "Billing", "History", "Team", "API", "MCP"],
          tabRightIcons: supportTeamMember ? undefined : mcpTabRightIcons,
          tabValue: value,
          setTabValue: setValue,
        }}
        allowAccess
      >
        <div
          style={{
            padding: 30,
            height: "100%",
            backgroundColor:
              !supportTeamMember && value === CreatorSettingsTab.HISTORY
                ? Colors.WHITE
                : Colors.WHITE1,
          }}
        >
          {supportTeamMember ? (
            <>
              <TabPanel value={value} index={0}>
                <ProfileAccountDetails setTabValue={setValue} />
              </TabPanel>
              <TabPanel value={value} index={1}>
                <TeamMembersPanel />
              </TabPanel>
              <TabPanel value={value} index={2}>
                <McpManagementPanel
                  supportMember
                  goToBillingTab={goToBillingTab}
                  continueTourOnApiTab={continueMcpTourOnApiTab}
                />
              </TabPanel>
            </>
          ) : (
            <>
              <TabPanel value={value} index={0}>
                <ProfileAccountDetails setTabValue={setValue} />
              </TabPanel>
              <TabPanel value={value} index={1}>
                <BillingPanel />
              </TabPanel>
              <TabPanel value={value} index={2}>
                <BillingHistoryPanel />
              </TabPanel>
              <TabPanel value={value} index={3}>
                <TeamMembersPanel />
              </TabPanel>
              <TabPanel value={value} index={4}>
                <ApiManagementPanel
                  goToMcpTab={goToMcpTab}
                  showMcpTourFinalStep={showMcpTourFinalStep}
                />
              </TabPanel>
              <TabPanel value={value} index={5}>
                <McpManagementPanel
                  goToBillingTab={goToBillingTab}
                  continueTourOnApiTab={continueMcpTourOnApiTab}
                />
              </TabPanel>
            </>
          )}
        </div>
      </CreatorModePanelWrapper>
      <HelpModal showModal={helpModal} setShowModal={setHelpModal} />
    </>
  );
};

export default CreatorSettingsPanel;
